import axios, { AxiosResponse } from "axios";
import { IUser, IGuest, ILogin, ICustomer, IResetPassword, IForgotPassword, ICustomerProfile } from "../models/user";
import { IShop } from "../models/shop";
import { IMainCategory, IPriceMatrixItem, ISubCategory } from "../models/category";
import { toast } from "react-toastify";
import { ITimeSlot, ITimeSlotsDTO, IOrder, IAppOrderEntity, IBundleOrderGroupEntity, INewOrder, IUpdateOrder } from "../models/order";
import { IVoucher, IVoucherListItem } from "../models/voucher";
import { ICustomerCard, INewCard, INewStripeCard } from "../models/cards";
import { IApiResponse, IApiResponseT } from "../models/apiresponse";
import { IContactForm, ISupportDetails } from "../models/support";
import { IRegularVisit, IScheduledVisit } from "../models/visit";
import { IDocument, IFAQs } from "../models/document";
import { IStripeIntent } from "../models/stripe_models";


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true
});

let isRefreshing = false;
let failedQueue: Array<(token: string) => void> = [];
api.interceptors.response.use(
  undefined, 
  async (error) => {
  if (error.message === "Network Error" && !error.response) {
    toast.error(
      "A network error occured. The server is either down or not responding."
    );
  }
  if(error.response?.status === 400)
    return Promise.reject(400);
  const originalRequest = error.config;
  if(!originalRequest || !error.response || error.response?.status !== 401 || originalRequest._retry)
    return Promise.reject(error);
  originalRequest._retry = true;
    if(!isRefreshing){
      isRefreshing = true;
      try {
        const isCustomer = window.sessionStorage.getItem("mdc_cus") ?? "0";
        const appKey = decodeURIComponent(window.sessionStorage.getItem("mdc_ak") ?? "");
        const shopCode = window.sessionStorage.getItem("mdc_sc") ?? "";
        const postCode = window.sessionStorage.getItem("mdc_pc") ?? "";
        let access_token= ""; 
        if(isCustomer === "1"){
          const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/refresh`,{appKey, shopCode},{withCredentials: true});
          access_token = res.data;
        } else if(isCustomer == "0"){
          const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/guest/refresh`,{appKey,shopCode,postCode})
          access_token = res.data;
        }
        window.sessionStorage.setItem("mdc_jwt",access_token);

        failedQueue.forEach((cb) => cb(access_token));
        failedQueue = [];
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (err) {
        sessionStorage.removeItem("mdc_jwt")
        failedQueue = [];
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return new Promise((resolve) => {
      failedQueue.push((token: string) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        resolve(axios(originalRequest));
      })
    })
  // const { status, headers } = error.response;
  // if (status === 404 && error.response) {
  //   // history.push("/");
  //   console.log(JSON.stringify(error))
  //   toast.error("The resource you are trying to access cannot be found! (404)");
  // }
  // if(status === 401 && headers.has('Token-Expired')){
  //   toast.error("Your session has expired! Please login again to continue.");
  //   const rootStore = useContext(RootStoreContext);
  //   const { logout } = rootStore.userStore;
  //   logout();
  // }

  /*if (
    status === 401 &&
    headers["www-authenticate"].includes(
      'Bearer error="invalid_token", error_description="The token expired at'
    )
  ) {
    window.localStorage.removeItem("jwt");
    history.push("/");
    console.log("Your session has expired, please login again.");
  }
  if (
    status === 400 &&
    config.method === "get" &&
    Object.prototype.hasOwnProperty.call(data.errors, "id")
  ) {
    // data.errors.hasOwnproperty("id") throws error so changed it
    history.push("/notfound");
  }
  if (status === 400 && config.method === "put") {
    console.log(error.response);
  }
*/

  // if (status === 500) {
  //   toast.error("A server error occured. Please contact the administrator");
  // }
  // throw error.response;
});

api.interceptors.request.use(
  (config) => {
    const token = window.sessionStorage.getItem("mdc_jwt");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// const responseBody = (response: AxiosResponse) => response.data;

const request = {
  get: (url: string) => api.get(url).then((res) => res.data),
  post: (url: string, body: {}) =>  api.post(url, body).then((res) => res.data),
  put: (url: string, body: {}) => api.put(url, body).then((res) => res.data),
  del: (url: string) => api.delete(url).then((res) => res.data)
};

const Shop = {
  list: (uid: string): Promise<IShop[]> => request.get(`/shop/${uid}`),
  checkshopuid: (uid: string) : Promise<boolean> => request.get(`/shop/check/${uid}`)
};

const User = {
  current: (): Promise<IUser> => request.get("/user"),
  login: (user: ILogin): Promise<IUser> => request.post("/user/login", user),
  guest: (user: IGuest): Promise<IUser> => request.post("/user/guest", user),
  forgotpassword: (forgotPwd: IForgotPassword): Promise<IApiResponse> => request.post("/user/password/forgot", forgotPwd),
  resetpassword: (reset: IResetPassword): Promise<IApiResponse> => request.post("/user/password/reset", reset),
  verifypassword: (password: string): Promise<boolean> => request.get(`/user/verify/${encodeURIComponent(password)}`),
};

const Customer = {
  customer: (): Promise<ICustomer> => request.get("/customer"),
  savecustomer: (customer: ICustomer): Promise<IUser> => request.post("/customer/update", customer),
  updatecustomer: (customer: ICustomerProfile) : Promise<boolean> => request.post("/customer/profile", customer),
  verifycustomer: (mobileno: string, emailid: string): Promise<boolean> => request.get(`/customer/verify/${encodeURIComponent(mobileno)}/${encodeURIComponent(emailid)}`),
  myoffers: () : Promise<IVoucherListItem[]> => request.get("/customer/offers"),
  myspecialprice: () : Promise<IPriceMatrixItem[]> => request.get("/customer/pricematrix")
};

const CustomerCards = {
  customercards: (): Promise<ICustomerCard[]> => request.get("/customercards"),
  addnewcard: (card: INewCard): Promise<IApiResponseT<ICustomerCard>> => request.post("/customercards/new",card),
  setdefaultcard: (card: ICustomerCard) => request.post("/customercards/default",card),
  deactivatecard: (card: ICustomerCard) => request.post("/customercards/inactive",card),
  createstripeintent: (): Promise<IApiResponseT<IStripeIntent>> => request.get("/customercards/stripe/intent"),
  cancelstripeintent: (id: string): Promise<IApiResponse> => request.del(`/customercards/stripe/intent/cancel/${id}`),
  addnewstripecard: (card: INewStripeCard): Promise<IApiResponseT<ICustomerCard>> => request.post("/customercards/new/stripe",card)
}

const Category = {
  maincategory: (): Promise<IMainCategory[]> => request.get("/category"),
  subcategory: (id: string): Promise<ISubCategory[]> =>
    request.get(`/category/${id}`),
  altercategory: (id: string): Promise<IMainCategory[]> =>
    request.get(`/category/alter/${id}`),
  alterations: (id: number): Promise<ISubCategory[]> =>
    request.get(`/category/alter/sub/${id}`),
};

const TimeSlot = {
  defaultCollection: (
    orderDate: string,
    postCode: string
  ): Promise<ITimeSlot> =>
    request.get(
      `/timeslot/collection/${encodeURIComponent(
        postCode
      )}?OrderDate=${encodeURIComponent(orderDate)}`
    ),
  defaultDelivery: (
    collectionDate: string,
    postCode: string
  ): Promise<ITimeSlot> =>
    request.get(
      `/timeslot/delivery/${encodeURIComponent(
        postCode
      )}?Collectiondate=${encodeURIComponent(collectionDate)}`
    ),
  weeklyCollection: (
    fromDate: string,
    postCode: string
  ): Promise<ITimeSlotsDTO[]> =>
    request.get(
      `/timeslot/collection/week/${encodeURIComponent(
        postCode
      )}?OrderDate=${encodeURIComponent(fromDate)}`
    ),
  weeklyDelivery: (fromDate: string, postCode: string): Promise<ITimeSlotsDTO[]> =>
    request.get(
      `/timeslot/delivery/week/${encodeURIComponent(
        postCode
      )}?CollectionDate=${encodeURIComponent(fromDate)}`
    ),
  slotfordate: (forDate: string, postCode: string): Promise<ITimeSlot[]> => request.get(`/timeslot/${encodeURIComponent(
        postCode
      )}?ForDate=${encodeURIComponent(forDate)}`)
};

const Order = {
  saveOrder: (order: INewOrder): Promise<number> => request.post("/order/new", order),
  updateOrder: (order: IUpdateOrder) : Promise<boolean> => request.post("/order/update", order),
  checkVoucher: (voucherCode: string, orderDate: string): Promise<IVoucher> => request.get(`/order/voucher/${encodeURIComponent(voucherCode)}?OrderDate=${encodeURIComponent(orderDate)}`),
  getOrder: (uid: string) : Promise<IOrder> => request.get(`/order/${uid}`),
  getUnPickedOrders: () : Promise<IAppOrderEntity[]> => request.get("/order/awaiting"),
  getPickedOrders: () : Promise<IAppOrderEntity[]> => request.get("/order/pickedup"),
  getOrdersInProcessing: () : Promise<IBundleOrderGroupEntity[]> => request.get("/order/processing"),
  getOrdersReady: () : Promise<IBundleOrderGroupEntity[]> => request.get("/order/ready"),
  getOrdersOnDelivery: () : Promise<IBundleOrderGroupEntity[]> => request.get("/order/ondelivery"),
  getOrdersDelivered: () : Promise<IBundleOrderGroupEntity[]> => request.get("/order/delivered"),
  cancelOrder: (id: string) : Promise<IAppOrderEntity> => request.del(`/order/cancel/${id}`)
};

const Visit = {
  regular: () : Promise<IRegularVisit[]> => request.get("/visit/regular"),
  scheduled: () : Promise<IScheduledVisit[]> => request.get("/visit/scheduled"),
  updateSlot: (visitUniqueRecId: string, slotUniqueRecId: string): Promise<boolean> => request.put(`/visit/update/${visitUniqueRecId}/${slotUniqueRecId}`,{}),
  delete: (uniqueRecId: string) : Promise<boolean> => request.del(`/visit/${uniqueRecId}`),
  visitById: (uid: string) : Promise<ITimeSlot> => request.get(`/visit/${uid}`),
}

const Support = {
  supportdetails: (appKey: string, shopCode: string): Promise<ISupportDetails> => request.get(`/support/${shopCode}?appKey=${encodeURIComponent(appKey)}`),
  sendcontactform: (contactForm: IContactForm) : Promise<boolean> => request.post("/support/contact",contactForm)
};

const Document = {
  terms: () : Promise<IDocument> => request.get("/document/terms"),
  privacy: () : Promise<IDocument> => request.get("/document/privacy"),
  faqs: () : Promise<IFAQs> => request.get("/document/faqs")
}

export default { Shop, User, Customer, CustomerCards, Category, TimeSlot, Order, Visit, Support, Document };
